import { zafClient, getZendeskUrl, popupError, getCurrentTicketId } from "../zendesk";
import { type IncidentProps } from "@interfaces/validationSchema";
import { getOrCreateUser } from "../user";
import { type CreateTicketResponse } from "@interfaces/ServiceOrder";
import type { ZendeskBrand, ZendeskTicket } from "@interfaces/tickets";

const dataURLToBlob = (dataURL: string): Blob => {
  const [header, base64] = dataURL.split(",");
  const mime = header.match(/data:(.*?);base64/)?.[1] || "application/octet-stream";
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

const normalizeToBlob = async (input: any, fallbackName = "upload") => {
  if (typeof input === "string" && input.startsWith("data:")) {
    const blob = dataURLToBlob(input);
    let name = fallbackName;

    if (blob.type.includes("png")) name += ".png";
    else if (blob.type.includes("jpeg") || blob.type.includes("jpg")) name += ".jpg";
    else if (blob.type.includes("gif")) name += ".gif";
    else if (blob.type.includes("webp")) name += ".webp";
    return { blob, name, type: blob.type, size: blob.size };
  }

  if (input && typeof input.arrayBuffer === "function") {
    const name = typeof input.name === "string" && input.name ? input.name : fallbackName;
    const type =
      typeof input.type === "string" && input.type ? input.type : "application/octet-stream";
    return { blob: input as Blob, name, type, size: (input as Blob).size };
  }
  if (typeof input === "string" && /^https?:\/\//.test(input)) {
    const res = await fetch(input);
    const blob = await res.blob();
    const url = new URL(input);
    const name = url.pathname.split("/").pop() || fallbackName;
    return { blob, name, type: blob.type || "application/octet-stream", size: blob.size };
  }
  throw new Error("Formato de anexo não suportado. Use File/Blob, dataURL ou URL http(s).");
};

const uploadWithFetch = async (blob: Blob, name: string, type: string) => {
  const url =
    `${getZendeskUrl()}/api/v2/uploads.json` +
    `?filename=${encodeURIComponent(name)}` +
    `&content_type=${encodeURIComponent(type || "application/octet-stream")}`;

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/binary",
      Accept: "application/json",
    },
    body: blob,
    credentials: "same-origin",
  });

  if (!resp.ok) {
    popupError("Erro inesperado. Por favor tente novamente mais tarde.", false);
  }

  const json = await resp.json();
  return json;
};

export const CreateIncident = async (body: IncidentProps) => {
  let userId: number | null = null;

  const user = await getOrCreateUser(body.nomeConsumidor, body.emailConsumidor, {
    cpf_3: body.cpf,
    endereco_2: `${body.endereco_2}, ${body.numero}`,
    cidade: body.cidade,
    _pessoa_fisica__bairro_de_cobranca: body.bairro,
    _pessoa_fisica__bairro_de_entrega: body.bairro,
    _pessoa_fisica__endereco_de_entrega: body.endereco_2,
    estado: body.estado,
    cep__endereco: body.cep,
  });
  userId = user.id;

  const uploadTokens: string[] = [];

  if (body.anexos && body.anexos.length > 0) {
    for (let i = 0; i < body.anexos.length; i++) {
      const raw = body.anexos[i];
      const { blob, name, type, size } = await normalizeToBlob(raw, `upload_${i}`);

      if (!size || size <= 15) {
        throw new Error(
          "Bytes inválidos antes do upload (tamanho <= 15). Origem não virou binário real."
        );
      }

      const json = await uploadWithFetch(blob, name, type);
      const att = json?.upload?.attachment;
      const token = json?.upload?.token;

      if (!token) {
        popupError("Erro inesperado, por favor tente novamente mais tarde.", false);
      }

      if (!att?.size || att.size <= 15) {
        throw new Error("Zendesk gravou tamanho 15 (corpo não chegou como binário).");
      }

      uploadTokens.push(token);
    }
  }

  const currentTicketId = await getCurrentTicketId();

  const ticketResponse = (await zafClient.request({
    url: `/api/v2/tickets/${currentTicketId}.json`,
    type: "GET",
  })) as { ticket: ZendeskTicket };

  const ticketData = ticketResponse.ticket;

  if (ticketData.brand_id) {
    const brandResponse = (await zafClient.request({
      url: `/api/v2/brands/${ticketData.brand_id}.json`,
      type: "GET",
    })) as { brand: ZendeskBrand };

    const brandData = brandResponse.brand;

    if (brandData?.id) {
      const brandId = brandData.id;

      const intendedFormId = 39380669191188;
      const brandFormIds = brandData.ticket_form_ids ?? [];

      const finalTicketFormId = brandFormIds.includes(intendedFormId)
        ? intendedFormId
        : brandFormIds.includes(ticketData.ticket_form_id)
          ? ticketData.ticket_form_id
          : brandFormIds[0];

      if (!finalTicketFormId) {
        throw new Error(
          `Brand ${brandId} não possui ticket_form_ids. Não dá pra definir ticket_form_id.`
        );
      }

      const response = await zafClient.request({
        url: `${getZendeskUrl()}/api/v2/tickets.json`,
        type: "POST",
        contentType: "application/json",
        data: JSON.stringify({
          ticket: {
            subject: body.assunto,
            comment: { body: body.descricao, public: false, uploads: uploadTokens },
            brand_id: brandId,
            requester_id: userId,
            priority: "normal",
            ticket_form_id: finalTicketFormId,
            type: "incident",
            problem_id: body.osPrincipal,
            custom_fields: [
              // { id: 38307473553940, value: body.codigoPosto },
              { id: 40237906749332, value: body.numeroSerie },
              { id: 46250937047700, value: body.osPrincipal },
              {
                id: 39594882139156,
                value:
                  body.statusSolicitacao === "aguardando_os"
                    ? "reparo_agendamento_status_aguardando_os"
                    : body.statusSolicitacao === "gerado_os"
                      ? "reparo_agendamento_status_gerado_os"
                      : "reparo_agendamento_status_erro",
              },
              { id: 38308977830420, value: body.nomeConsumidor },
              { id: 38308138125972, value: body.emailConsumidor },
              { id: 38307424050708, value: body.cep },
              { id: 38308170443540, value: body.endereco_2 },
              { id: 38309114260884, value: body.numero === "" ? "S/N" : body.numero },
              { id: 38307230752404, value: body.bairro },
              { id: 38307431748756, value: body.cidade },
              { id: 38336942532116, value: body.estado },
              { id: 38336801044884, value: body.telefoneConsumidor },
              { id: 39380284182676, value: body.notaFiscal },
              { id: 39380172069140, value: body.dataCompra },
              { id: 40237972415508, value: body.aparenciaProduto },
              { id: 40237935381268, value: body.defeitoReclamado },
              { id: 38303107042452, value: body.descricaoProduto },
              { id: 40237889387284, value: body.referenciaProduto },
            ],
          },
        }),
      });

      return response as CreateTicketResponse;
    }

    return null as unknown as CreateTicketResponse;
  }
};
